// „Wer hat Zugriff?" — die eine Stelle, an der sichtbar wird, wer ein Projekt,
// eine Aufgabenliste oder einen Space sehen kann, und WOHER dieses Recht kommt.
//
// WARUM DIESER DIALOG EXISTIERT
// Zugriff entsteht in juuplan aus sieben Quellen (lib/access.ts): Eigentümer,
// direkte Freigabe, Space-Mitgliedschaft, Abteilungs-Freigabe, Gesamt-
// organisation, Administration, Gast-Einladung — plus Bots, die die Rechte
// ihres Eigentümers erben. Sichtbar war davon bisher nur die direkte Freigabe.
// Wer wissen wollte, ob eine Notiz in einem Projekt „intern" bleibt, konnte das
// an der Oberfläche nicht beantworten.
//
// Die Gruppierung folgt der HERKUNFT, nicht dem Alphabet: die eigentliche
// Neuigkeit ist nicht, DASS jemand Zugriff hat, sondern warum. Wer eine Gruppe
// „Über den Space Allgemein (37)" sieht, versteht sofort, dass die Freigabe
// nicht an diesem Projekt hängt — und wo er sie ändern müsste.
//
// Die Rollen sind hier bewusst NICHT bearbeitbar. Ändern bleibt dort, wo es
// hingehört (Teilen-Dialog, Space-Einstellungen, Administration) — dieser
// Dialog ist eine Auskunft, kein zweiter Verwaltungsweg.

import { useEffect, useMemo, useState } from "preact/hooks";
import {
  Bot,
  Building2,
  Crown,
  Globe,
  ListChecks,
  Search,
  Shield,
  UserPlus,
  Users,
} from "lucide-preact";
import type { AccessEntry, AccessOverview, AccessSource } from "@juuplan/shared";
import { Modal } from "./Modal";
import { UserAvatar } from "./UserAvatar";
import { getListAccess, getProjectAccess, getSpaceAccess } from "../lib/api";

interface Props {
  subject:
    | { kind: "project"; id: number }
    | { kind: "list"; id: number }
    | { kind: "space"; id: number };
  onClose: () => void;
}

const ROLE_LABEL: Record<AccessEntry["role"], string> = {
  owner: "Eigentümer",
  editor: "Bearbeiter",
  viewer: "Betrachter",
};

/** Überschrift einer Herkunfts-Gruppe. Trägt den Klartext-Namen der Quelle. */
function groupTitle(s: AccessSource): string {
  switch (s.via) {
    case "owner":
      return "Eigentümer";
    case "member":
      return "Direkt für dieses Projekt freigegeben";
    case "space":
      return s.label ? `Über den Space „${s.label}"` : "Über den Space";
    case "department":
      if (s.is_org_wide) return "Über die Freigabe an die Gesamtorganisation";
      return s.label ? `Über die Abteilung „${s.label}"` : "Über eine Abteilung";
    case "org":
      return "Organisationsweit sichtbar";
    case "admin":
      return "Administration";
    case "invite":
      return "Externe Gäste";
    case "bot":
      return "Bots";
    case "task-owner":
      return "Über eigene Aufgaben in dieser Liste";
  }
}

/** Ein Satz, der die Gruppe erklärt — die Herkunft ist der eigentliche Inhalt. */
function groupHint(s: AccessSource, kind: AccessOverview["subject"]["kind"]): string {
  switch (s.via) {
    case "owner":
      return kind === "project"
        ? "Voller Zugriff, unabhängig von der Sichtbarkeit."
        : "Voller Zugriff auf die Liste.";
    case "member":
      return "Namentlich hinzugefügt. Diese Freigabe gilt genau hier und schlägt eine abweichende Space-Rolle.";
    case "space":
      return "Ergibt sich aus der Mitgliedschaft im Space — nicht aus einer Freigabe an diesem Objekt. Zu ändern in den Space-Einstellungen.";
    case "department":
      return s.is_org_wide
        ? "Der Space ist an die Gesamtorganisation freigegeben: damit zählt jede aktive interne Person als Mitglied, ohne eigenen Eintrag."
        : "Ergibt sich aus der Abteilungs-Freigabe auf den Space. Zu ändern bei der Abteilung oder am Space.";
    case "org":
      return "Die Sichtbarkeit des Projekts ist organisationsweit: damit ist jede aktive interne Person Bearbeiter.";
    case "admin":
      return "Administratoren erreichen jedes Projekt. Bei privaten Projekten wird dieser Zugriff protokolliert.";
    case "invite":
      return "Externe Personen mit gültiger Einladung. Zu widerrufen bei den Gästen des Projekts.";
    case "bot":
      return "Ein Bot sieht genau das, was seine Eigentümerin oder sein Eigentümer sieht — eine eigene Freigabe an den Bot bleibt wirkungslos.";
    case "task-owner":
      return "Die Liste gehört jemand anderem, diese Personen erreichen aber ihre eigenen Aufgaben darin.";
  }
}

function GroupIcon(props: { via: AccessSource["via"] }): preact.JSX.Element {
  const size = 15;
  switch (props.via) {
    case "owner":
      return <Crown size={size} aria-hidden="true" />;
    case "member":
      return <UserPlus size={size} aria-hidden="true" />;
    case "space":
      return <Users size={size} aria-hidden="true" />;
    case "department":
      return <Building2 size={size} aria-hidden="true" />;
    case "org":
      return <Globe size={size} aria-hidden="true" />;
    case "admin":
      return <Shield size={size} aria-hidden="true" />;
    case "invite":
      return <UserPlus size={size} aria-hidden="true" />;
    case "bot":
      return <Bot size={size} aria-hidden="true" />;
    case "task-owner":
      return <ListChecks size={size} aria-hidden="true" />;
  }
}

/** Nebenquellen-Text: „außerdem über den Space Allgemein (Bearbeiter)". */
function alsoText(s: AccessSource): string {
  const role = ROLE_LABEL[s.role];
  switch (s.via) {
    case "space":
      return `Space „${s.label ?? "?"}" (${role})`;
    case "department":
      return s.is_org_wide
        ? `Gesamtorganisation (${role})`
        : `Abteilung „${s.label ?? "?"}" (${role})`;
    case "member":
      return `direkte Freigabe (${role})`;
    case "org":
      return `organisationsweite Sichtbarkeit (${role})`;
    case "admin":
      return "Administration";
    case "owner":
      return "Eigentümer";
    case "invite":
      return `Einladung (${role})`;
    case "bot":
      return "Bot-Vererbung";
    case "task-owner":
      return "eigene Aufgaben";
  }
}

/**
 * Gruppen-Schlüssel: `via` PLUS die Quellen-Id, damit zwei Abteilungen zwei
 * eigene Abschnitte bekommen statt in einem „Über eine Abteilung" zu
 * verschwimmen — dort steht ja gerade die Antwort auf „warum".
 */
function groupKey(s: AccessSource): string {
  return `${s.via}:${s.ref_id ?? 0}`;
}

/** Gruppen, die lang werden können, starten eingeklappt. */
const COLLAPSE_ABOVE = 8;

export function AccessOverviewDialog(props: Props): preact.JSX.Element {
  const { subject, onClose } = props;
  const [data, setData] = useState<AccessOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    let alive = true;
    const load =
      subject.kind === "project"
        ? getProjectAccess(subject.id)
        : subject.kind === "list"
          ? getListAccess(subject.id)
          : getSpaceAccess(subject.id);
    load
      .then((res) => {
        if (alive) setData(res);
      })
      .catch(() => {
        // GET-Fehler toastet api() NICHT (lib/api.ts: nur schreibende
        // Methoden) — der eigene `error`-Zustand hier IST die Rückmeldung,
        // sichtbar direkt im Dialog statt zusätzlich als Toast.
        if (alive) setError("Die Zugriffsliste konnte nicht geladen werden.");
      });
    return () => {
      alive = false;
    };
  }, [subject.kind, subject.id]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!data) return [];
    if (!q) return data.entries;
    return data.entries.filter(
      (e) =>
        e.user.name.toLowerCase().includes(q) ||
        e.user.email.toLowerCase().includes(q)
    );
  }, [data, query]);

  // Reihenfolge der Gruppen ergibt sich aus der Sortierung der Einträge, die
  // der Server nach Quellen-Präzedenz liefert — die Anzeige erfindet keine
  // eigene Rangfolge.
  const groups = useMemo(() => {
    const map = new Map<string, { source: AccessSource; entries: AccessEntry[] }>();
    for (const e of filtered) {
      const key = groupKey(e.source);
      const g = map.get(key);
      if (g) g.entries.push(e);
      else map.set(key, { source: e.source, entries: [e] });
    }
    return [...map.values()];
  }, [filtered]);

  const title = "Wer hat Zugriff?";

  return (
    <Modal title={title} onClose={onClose} size="lg">
      {error && (
        <p class="text-sm text-[color:var(--color-error)]">{error}</p>
      )}

      {!data && !error && (
        <p class="text-sm text-[color:var(--color-on-surface-variant)]">Wird geladen…</p>
      )}

      {data && (
        <div class="space-y-4">
          <SubjectHeader subject={data.subject} total={data.entries.length} />

          <label class="relative block">
            <Search
              size={16}
              class="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--color-on-surface-variant)]"
              aria-hidden="true"
            />
            <input
              type="search"
              value={query}
              onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
              placeholder="Person suchen…"
              aria-label="Person suchen"
              class="w-full min-w-0 rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] py-2 pl-9 pr-3 text-base sm:text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
            />
          </label>

          {groups.length === 0 && (
            <p class="text-sm text-[color:var(--color-on-surface-variant)]">
              {query.trim()
                ? "Keine Person mit diesem Namen hat hier Zugriff."
                : "Niemand hat hier Zugriff."}
            </p>
          )}

          {groups.map((g) => {
            const key = groupKey(g.source);
            const long = g.entries.length > COLLAPSE_ABOVE;
            const open = expanded.has(key) || !long || query.trim().length > 0;
            const shown = open ? g.entries : g.entries.slice(0, 3);
            return (
              <section key={key} class="space-y-2">
                <header class="flex items-start gap-2 min-w-0">
                  <span class="mt-0.5 text-[color:var(--color-on-surface-variant)]">
                    <GroupIcon via={g.source.via} />
                  </span>
                  <div class="min-w-0 flex-1">
                    <h3 class="text-sm font-semibold text-[color:var(--color-on-surface)]">
                      {groupTitle(g.source)}{" "}
                      <span class="font-normal text-[color:var(--color-on-surface-variant)]">
                        ({g.entries.length})
                      </span>
                    </h3>
                    <p class="text-xs text-[color:var(--color-on-surface-variant)]">
                      {groupHint(g.source, data.subject.kind)}
                    </p>
                  </div>
                </header>

                <ul class="space-y-1">
                  {shown.map((e) => (
                    <EntryRow key={e.user.id} entry={e} />
                  ))}
                </ul>

                {long && !open && (
                  <button
                    type="button"
                    onClick={() =>
                      setExpanded((prev) => new Set(prev).add(key))
                    }
                    class="text-xs font-medium text-[color:var(--color-primary)] hover:underline"
                  >
                    Alle {g.entries.length} anzeigen
                  </button>
                )}
              </section>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

/** Kopf: um welches Objekt geht es, und wie weit reicht sein Zugriff? */
function SubjectHeader(props: {
  subject: AccessOverview["subject"];
  total: number;
}): preact.JSX.Element {
  const { subject, total } = props;

  const kindLabel =
    subject.kind === "project"
      ? "Projekt"
      : subject.kind === "list"
        ? "Aufgabenliste"
        : "Space";

  let scope: string;
  if (subject.kind === "project") {
    scope =
      subject.visibility === "private"
        ? "Privat — nur Eigentümer und namentlich freigegebene Personen. Mitgliedschaft im Space genügt hier NICHT."
        : subject.visibility === "org"
          ? "Organisationsweit — jede aktive interne Person ist Bearbeiter."
          : `Geteilt — wer den Space „${subject.space?.name ?? "?"}" erreicht, erreicht auch dieses Projekt.`;
  } else if (subject.kind === "list") {
    scope = subject.shared
      ? `Geteilte Liste — der Zugriff kommt vom Space „${subject.space?.name ?? "?"}".`
      : "Persönliche Liste — sie gehört ihrem Eigentümer und wird nicht über den Space geteilt.";
  } else {
    const r = subject.reach;
    scope = r
      ? `Wer diesen Space erreicht, erreicht damit ${r.projects_shared} ${
          r.projects_shared === 1 ? "Projekt" : "Projekte"
        } und ${r.lists_shared} geteilte ${
          r.lists_shared === 1 ? "Liste" : "Listen"
        }.${
          r.projects_private_excluded > 0
            ? ` ${r.projects_private_excluded} private ${
                r.projects_private_excluded === 1 ? "Projekt" : "Projekte"
              } bleiben ausgenommen.`
            : ""
        }`
      : "";
  }

  return (
    <div class="rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-3 py-2.5">
      <p class="text-sm font-medium text-[color:var(--color-on-surface)] break-words">
        {kindLabel} „{subject.name}"
      </p>
      <p class="mt-0.5 text-xs text-[color:var(--color-on-surface-variant)]">{scope}</p>
      <p class="mt-1.5 text-xs text-[color:var(--color-on-surface-variant)]">
        {total} {total === 1 ? "Person bzw. Bot hat" : "Personen bzw. Bots haben"} Zugriff.
        Ändern lässt sich das dort, wo die jeweilige Freigabe herkommt.
      </p>
    </div>
  );
}

/** Eine Zeile: wer, mit welcher Rolle, und woher sonst noch. */
function EntryRow(props: { entry: AccessEntry }): preact.JSX.Element {
  const { entry: e } = props;
  return (
    <li class="flex items-start gap-2.5 rounded-md px-2 py-1.5 hover:bg-[color:var(--color-surface-container-low)] min-w-0">
      <span class="mt-0.5 shrink-0">
        <UserAvatar
          userId={e.user.id}
          name={e.user.name}
          avatarUrl={e.user.avatar_url}
          isBot={e.user.is_bot}
          botOwnerId={e.user.bot_owner_id}
          size={24}
        />
      </span>
      <div class="min-w-0 flex-1">
        <div class="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
          <span class="text-sm font-medium text-[color:var(--color-on-surface)] truncate">
            {e.user.name}
          </span>
          <RoleChip entry={e} />
          {e.is_external && <Badge tone="warn">Gast</Badge>}
          {e.user.is_bot && <Badge tone="neutral">Bot</Badge>}
          {e.is_admin && <Badge tone="neutral">Admin</Badge>}
          {!e.is_active && <Badge tone="warn">gesperrt</Badge>}
        </div>
        <p class="text-xs text-[color:var(--color-on-surface-variant)] truncate">
          {e.user.is_bot && e.source.label
            ? `erbt von ${e.source.label}`
            : e.user.email}
        </p>
        {e.source.via === "invite" && e.source.task_id != null && (
          <p class="text-xs text-[color:var(--color-on-surface-variant)]">
            Sieht nur eine einzelne Aufgabe, nicht das Projekt.
          </p>
        )}
        {e.source.via === "invite" && e.source.expires_at && (
          <p class="text-xs text-[color:var(--color-on-surface-variant)]">
            Einladung gültig bis{" "}
            {new Date(e.source.expires_at).toLocaleDateString("de-DE")}
          </p>
        )}
        {e.also.length > 0 && (
          <p class="text-xs text-[color:var(--color-on-surface-variant)]">
            außerdem über {e.also.map(alsoText).join(", ")}
          </p>
        )}
        {!e.is_active && (
          <p class="text-xs text-[color:var(--color-on-surface-variant)]">
            Kann sich nicht anmelden, solange das Konto gesperrt ist.
          </p>
        )}
      </div>
    </li>
  );
}

function RoleChip(props: { entry: AccessEntry }): preact.JSX.Element {
  const { entry: e } = props;
  const cls = e.can_write
    ? "bg-[color:var(--color-primary-container)] text-[color:var(--color-on-primary-container)]"
    : "bg-[color:var(--color-surface-container-high)] text-[color:var(--color-on-surface-variant)]";
  return (
    <span
      class={`shrink-0 rounded px-1.5 py-0.5 text-micro font-medium ${cls}`}
      title={e.can_write ? "Kann Inhalte ändern" : "Kann nur lesen"}
    >
      {ROLE_LABEL[e.role]}
    </span>
  );
}

function Badge(props: {
  tone: "warn" | "neutral";
  children: preact.ComponentChildren;
}): preact.JSX.Element {
  const cls =
    props.tone === "warn"
      ? "bg-[color:var(--color-tertiary-container)] text-[color:var(--color-on-tertiary-container)]"
      : "bg-[color:var(--color-surface-container-high)] text-[color:var(--color-on-surface-variant)]";
  return (
    <span class={`shrink-0 rounded px-1.5 py-0.5 text-micro font-medium ${cls}`}>
      {props.children}
    </span>
  );
}
